import type { LegalDoc } from '../types';

// Transcribed 1:1 from "06 - Politica de Cookies.pdf" (MEC Vault/legal/app-legal).
export const cookies: LegalDoc = {
  slug: '/cookies',
  title: 'Política de Cookies',
  version: '1.0',
  updated: 'Julio de 2026',
  entity: 'Mi Envío Cuba',
  seoDescription:
    'Política de Cookies de Mi Envío Cuba: qué cookies y tecnologías similares usamos, para qué, y cómo puede controlarlas.',
  closing: 'Fin de la Política de Cookies.',
  sections: [
    {
      id: 'seccion-1',
      number: '1',
      title: 'Qué son las cookies y tecnologías similares',
      blocks: [
        {
          t: 'p',
          text: 'Las “cookies” son pequeños archivos de texto que un sitio web coloca en su navegador para recordar información sobre su visita. Tecnologías similares incluyen: identificadores de dispositivo móvil, tokens de notificaciones push, píxeles de rastreo, y almacenamiento local del navegador (localStorage, sessionStorage).',
        },
        {
          t: 'p',
          text: 'Esta Política de Cookies describe qué cookies y tecnologías similares usa Mi Envío Cuba, para qué las usa, y cómo usted puede controlarlas.',
        },
        {
          t: 'p',
          text: 'Esta Política complementa nuestra Política de Privacidad (v1.0) y forma parte integrante de los Términos de Servicio.',
        },
      ],
    },
    {
      id: 'seccion-2',
      number: '2',
      title: 'Dónde usamos cookies',
      blocks: [
        {
          t: 'ul',
          items: [
            'En el sitio web mienviocuba.com y en subdominios asociados.',
            'En la aplicación web Mi Dashboard (usada por agencias).',
            'En Mi Cargo (usada por transitarias).',
          ],
        },
        {
          t: 'p',
          text: 'En las aplicaciones móviles (Mi Envío Cuba, Mi Admin, Mi Restaurante), no usamos cookies web en el sentido tradicional, pero usamos tecnologías equivalentes: identificadores del dispositivo, tokens de notificaciones push, y almacenamiento local de la aplicación.',
        },
      ],
    },
    {
      id: 'seccion-3',
      number: '3',
      title: 'Categorías de cookies que usamos',
      blocks: [
        { t: 'h3', text: '3.1 Cookies estrictamente necesarias' },
        {
          t: 'p',
          text: 'Son esenciales para el funcionamiento de la Aplicación. Sin ellas, no podemos ofrecer los servicios que usted solicita.',
        },
        {
          t: 'p',
          text: '**Ejemplos de uso:** iniciar y mantener su sesión, guardar sus preferencias de idioma, verificar autenticación, prevenir fraude y ataques automatizados, mantener el carrito de compra o pedido en curso.',
        },
        {
          t: 'p',
          text: '**Base legal:** interés legítimo / necesidad contractual. No requieren consentimiento previo bajo leyes de privacidad europeas.',
        },
        { t: 'p', text: '**Duración típica:** desde la sesión actual hasta un año.' },
        { t: 'h3', text: '3.2 Cookies de funcionalidad' },
        {
          t: 'p',
          text: 'Permiten funciones mejoradas y personalización, sin ser estrictamente esenciales.',
        },
        {
          t: 'p',
          text: '**Ejemplos de uso:** recordar la ciudad de destino que usted usó la última vez, preferencias de visualización, la agencia que usted eligió recientemente.',
        },
        { t: 'p', text: '**Duración típica:** desde algunas semanas hasta un año.' },
        { t: 'h3', text: '3.3 Cookies de análisis' },
        {
          t: 'p',
          text: 'Nos ayudan a entender cómo se usa la Aplicación en agregado (páginas visitadas, tiempo de sesión, errores encontrados) para mejorar el producto. **No las usamos para identificar a usuarios individuales para fines de marketing.**',
        },
        {
          t: 'p',
          text: '**Ejemplos de proveedores:** proveedores de analítica reconocidos en la industria.',
        },
        { t: 'p', text: '**Duración típica:** hasta 24 meses.' },
        { t: 'h3', text: '3.4 Cookies de marketing y publicidad' },
        {
          t: 'p',
          text: '**Actualmente no usamos cookies de marketing o publicidad de terceros** para segmentar anuncios ni para retargeting. Si esto cambia, actualizaremos esta Política y le solicitaremos consentimiento cuando la ley aplicable lo requiera.',
        },
      ],
    },
    {
      id: 'seccion-4',
      number: '4',
      title: 'Cookies de terceros',
      blocks: [
        {
          t: 'p',
          text: 'Algunos de los servicios que usamos colocan sus propias cookies o tecnologías similares:',
        },
        {
          t: 'ul',
          items: [
            '**Proveedores de infraestructura y autenticación** — para autenticación de usuario, analítica y notificaciones push.',
            '**Procesadores de pago** — para procesar pagos y prevenir fraude de manera segura.',
            '**Servicios de mensajería** (proveedores de correo transaccional, SMS) — colocan identificadores para medir apertura y entrega de comunicaciones.',
          ],
        },
        {
          t: 'p',
          text: 'Cada uno de estos terceros tiene su propia política de privacidad y de cookies. No controlamos las prácticas de terceros más allá de lo contractualmente acordado.',
        },
      ],
    },
    {
      id: 'seccion-5',
      number: '5',
      title: 'Su control sobre cookies',
      blocks: [
        { t: 'h3', text: '5.1 Configuración del navegador' },
        {
          t: 'p',
          text: 'Puede aceptar, rechazar, eliminar o restringir cookies desde los ajustes de su navegador. Consulte la documentación de su navegador:',
        },
        {
          t: 'ul',
          items: [
            '**Chrome:** chrome://settings/cookies',
            '**Safari:** Ajustes → Safari → Privacidad',
            '**Firefox:** Ajustes → Privacidad y Seguridad',
            '**Edge:** Ajustes → Cookies y permisos del sitio',
          ],
        },
        {
          t: 'p',
          text: 'Tenga en cuenta que rechazar cookies estrictamente necesarias puede impedir el funcionamiento correcto de la Aplicación.',
        },
        { t: 'h3', text: '5.2 Do Not Track' },
        {
          t: 'p',
          text: 'Respetamos las señales “Do Not Track” del navegador y las opciones del sistema operativo móvil (App Tracking Transparency en iOS, Limit Ad Tracking en Android) para tecnologías de rastreo comparables.',
        },
        { t: 'h3', text: '5.3 Aplicaciones móviles' },
        { t: 'p', text: 'En las aplicaciones móviles, puede:' },
        {
          t: 'ul',
          items: [
            'Deshabilitar notificaciones push desde los ajustes del sistema operativo o de la aplicación.',
            'Revocar permisos de aplicación (ubicación, cámara, contactos, etc.) desde los ajustes del sistema operativo.',
            'Reiniciar el identificador de publicidad del dispositivo desde los ajustes del sistema operativo.',
            'Solicitar la eliminación de su cuenta y datos asociados escribiendo al correo indicado en la Sección 7.',
          ],
        },
        { t: 'h3', text: '5.4 Consentimiento donde aplique' },
        {
          t: 'p',
          text: 'En jurisdicciones que requieren consentimiento previo para cookies no esenciales (por ejemplo, la Unión Europea bajo la Directiva de ePrivacy y el RGPD), le mostraremos un banner de consentimiento que le permita aceptar, rechazar, o personalizar las categorías de cookies antes de activarlas.',
        },
      ],
    },
    {
      id: 'seccion-6',
      number: '6',
      title: 'Modificaciones',
      blocks: [
        {
          t: 'p',
          text: 'MEC puede modificar esta Política en cualquier momento. Los cambios materiales se notifican dentro de la Aplicación o en el sitio web. La política vigente es la publicada al momento de su visita.',
        },
      ],
    },
    {
      id: 'seccion-7',
      number: '7',
      title: 'Contacto',
      blocks: [
        {
          t: 'p',
          text: 'Para preguntas sobre esta Política de Cookies o sobre el uso de cookies en la Aplicación:',
        },
        {
          t: 'ul',
          items: ['**Correo:** support@mienviocuba.com', '**Teléfono:** +1 (786) 519-1709'],
        },
      ],
    },
  ],
};
